<template>
  <PmDataModalResourcePure
    :id="id"
    :is-loading="isLoading"
    :resource="resource?.resourceFunction?.caption"
    :type="resource?.resourceFunction?.resourceType?.caption"
    :quantity="resource?.quantity"
    :start-date="parseServerDateString(resource?.startDate)"
    :end-date="parseServerDateString(resource?.endDate)"
    :created-date="parseServerDateString(resource?.created)"
    :created-by="resource?.createdBy?.displayName"
    :edited-date="parseServerDateString(resource?.updated)"
    :edited-by="resource?.updatedBy?.displayName"
    :project="project"
    :stock-type-service="stockTypeService"
    :purchase-order="purchaseOrder"
    :job="job"
    :resource-allocations="resourceAllocations"
    @open-project="
      (projectId) =>
        EventHub.$emit(EVENT.DATA_MODAL_SHOW, {
          type: DATA_MODAL_TYPE.PROJECT,
          id: projectId,
        })
    "
    @open-stock-type-service="openStockTypeService"
    @open-purchase-order="openPurchaseOrder"
    @open-job="
      (jobId) =>
        EventHub.$emit(EVENT.DATA_MODAL_SHOW, {
          type: DATA_MODAL_TYPE.JOB,
          id: jobId,
        })
    "
    @open-resource-allocation="
      (resourceAllocationId) =>
        EventHub.$emit(EVENT.DATA_MODAL_SHOW, {
          type: DATA_MODAL_TYPE.RESOURCE_ALLOCATION,
          id: resourceAllocationId,
        })
    "
  >
    <template #note>
      <PmNoteResource :id="props.id" />
    </template>
  </PmDataModalResourcePure>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import PmDataModalResourcePure, {
  type Props as PropsDataModalResourcePure,
} from './PmDataModalResourcePure.vue'
import EventHub from '@/eventHub'
import { lookup } from '@/utilities/misc'
import { parseServerDateString } from '@/utilities/date'
import {
  STATUS_RESOURCE_ALLOCATION_LOOKUP,
  STATUS_PROJECT_LOOKUP,
  STATUS_JOB_LOOKUP,
  DATA_MODAL_TYPE,
  RESOURCE_TYPE_LOOKUP,
  type ResourceType,
} from '@/constants/persoplan'
import { EVENT } from '@/constants/events'
import {
  DataModalResourceDocument,
  type DataModalResourceQuery,
} from '../../../../../generated/graphql'
import type { Get } from 'type-fest'
import { getDisplayNameOfAddress } from '../../../../utilities/string'
import PmNoteResource from '@/components/PmNote/PmNoteResource.vue'

export type Props = {
  id: number
}

const props = withDefaults(defineProps<Props>(), {})

const resourceQuery = useQuery(
  DataModalResourceDocument,
  () => ({
    id: props.id,
  }),
  () => ({
    // Don't update the cache, which might change the calendar view.
    fetchPolicy: 'no-cache',
    // notifyOnNetworkStatusChange is needed so $apollo.loading gets updated
    notifyOnNetworkStatusChange: true,
  })
)

const resource = computed(() => resourceQuery.result.value?.resource)

const isLoading = computed(() => {
  return resourceQuery.loading.value === true
})

const stockTypeService = computed(() => {
  if (!resource.value?.stockTypeService) return

  const result: PropsDataModalResourcePure['stockTypeService'] = {
    id: resource.value.stockTypeService.id,
    number: resource.value.stockTypeService.number,
    label: resource.value.stockTypeService.caption,
  }

  return result
})

const purchaseOrder = computed(() => {
  if (!resource.value?.purchaseOrder) return

  const result: PropsDataModalResourcePure['purchaseOrder'] = {
    id: resource.value.purchaseOrder.id,
    number: resource.value.purchaseOrder.number,
    label: resource.value.purchaseOrder.caption,
  }

  return result
})

/**
 * Project
 */
const projectStatus = computed(() => {
  return lookup(
    resource.value?.job?.project?.projectState?.id,
    STATUS_PROJECT_LOOKUP
  )
})

const project = computed(() => {
  if (!resource.value?.job?.project) return

  const result: PropsDataModalResourcePure['project'] = {
    id: resource.value.job.project?.id,
    number: resource.value.job.project?.number,
    label: `${resource.value.job.project?.caption} (${resource.value.job.project?.projectState?.caption})`,
    status: projectStatus.value,
  }

  return result
})

/**
 * Job
 */
const job = computed(() => {
  if (!resource.value?.job) return

  const result: PropsDataModalResourcePure['job'] = {
    id: resource.value.job.id,
    title: resource.value.job.caption,
    number: resource.value.job.number,
    status: lookup(resource.value.job.jobState?.id, STATUS_JOB_LOOKUP),
  }

  return result
})

/**
 * ResourceAllocations
 */
const resourceAllocations = computed(() => {
  const result: PropsDataModalResourcePure['resourceAllocations'] = []

  resource.value?.resourceAllocations?.forEach((resourceAllocation) => {
    if (!resourceAllocation) return

    const resourceType = lookup(
      resource.value?.resourceFunction?.resourceType?.id,
      RESOURCE_TYPE_LOOKUP
    )

    if (!resourceType) {
      console.warn('resourceType is undefined')
      return
    }

    const resourceAllocationNormalized = normalizeResourceAllocation(
      resourceAllocation,
      { resourceType }
    )

    if (!resourceAllocationNormalized) return

    result.push(resourceAllocationNormalized)
  })

  return result
})

type ResourceAllocation = Get<
  DataModalResourceQuery,
  'resource.resourceAllocations[0]'
>

function openStockTypeService() {
  if (!stockTypeService.value) return

  EventHub.$emit(EVENT.DATA_MODAL_SHOW, {
    type: DATA_MODAL_TYPE.STOCK_TYPE_SERVICE,
    id: stockTypeService.value.id,
  })
}

function openPurchaseOrder() {
  if (!purchaseOrder.value) return

  EventHub.$emit(EVENT.DATA_MODAL_SHOW, {
    type: DATA_MODAL_TYPE.PURCHASE_ORDER,
    id: purchaseOrder.value.id,
  })
}

function normalizeResourceAllocation(
  resourceAllocation: ResourceAllocation,
  options: { resourceType: ResourceType }
) {
  if (!resourceAllocation) return

  let name: string | undefined = undefined

  if (options.resourceType === 'address')
    name = normalizeNameOfAddress(resourceAllocation)

  if (options.resourceType === 'vehicle')
    name = normalizeNameOfVehicle(resourceAllocation)

  const result: Get<PropsDataModalResourcePure, 'resourceAllocations[0]'> = {
    id: resourceAllocation.id,
    name: name,
    status: lookup(
      resourceAllocation.resourceAllocationState?.id,
      STATUS_RESOURCE_ALLOCATION_LOOKUP
    ),
  }

  return result
}

function normalizeNameOfAddress(resourceAllocation: ResourceAllocation) {
  if (!resourceAllocation) return

  let name = getDisplayNameOfAddress(resourceAllocation.address)

  const vehicleName =
    resourceAllocation.resourceAllocationDriver?.vehicle?.caption

  if (vehicleName) {
    name = `${name}@${vehicleName}`
  }

  return name
}

function normalizeNameOfVehicle(resourceAllocation: ResourceAllocation) {
  if (!resourceAllocation) return

  let name = resourceAllocation.vehicle?.caption ?? undefined

  const driverName = getDisplayNameOfAddress(
    resourceAllocation?.resourceAllocationDriver?.address
  )

  if (driverName) {
    name = `${name}@${driverName}`
  }

  return name
}
</script>

<style lang="scss" scoped>
.PmDataModalResource {
  $block: &;
}
</style>
